// Zvuková vrstva UNO: ruka na šipkách, výběr barvy, zvuky podle barvy a hlášení pro čtečku.
import type { GameEvent } from "../../../games/types.ts";
import type { Card, Color, UnoView } from "../../../games/uno/meta.ts";
import { announce, say } from "../../announce.ts";
import { play, queue } from "../../audio.ts";
import {
  cardName,
  cards,
  COLOR_NAME,
  COLORS,
  countsText,
  hintText,
  panFor,
  playable,
  playerName,
  resultText,
  orderText,
  topText,
  turnText,
  type Model,
} from "./model.ts";
import "./sounds.ts";

export const HELP_TEXT =
  "Nápověda. Šipky vlevo a vpravo: karty v ruce. Šipky nahoru a dolů: další nebo předchozí hratelná karta. Home a End: první a poslední karta. " +
  "Enter nebo mezerník: zahrát kartu. U černé karty vybereš barvu šipkami nahoru a dolů a potvrdíš Enterem, Escape výběr zruší. " +
  "L: líznout kartu. P: nechat líznutou kartu a předat tah. V: vrchní karta. R: stav hry. T: pořadí hráčů, kdo je na tahu a kdo bude další. H: tato nápověda. " +
  "Při pohybu po ruce má hratelná karta vlastní zvuk, ostatní běžný zvuk procházení. Karta zní vlevo nebo vpravo podle pozice v ruce. UNO se volá samo. " +
  "Tabulátor opustí hrací plochu. Pokud klávesy nefungují, zapni režim aplikace klávesou NVDA plus mezerník.";


export class SoundLayer {
  private model: Model;
  private board: HTMLElement;
  private onChange: () => void;
  private onKey = (e: KeyboardEvent) => this.handleKey(e);

  constructor(model: Model, board: HTMLElement, onChange: () => void) {
    this.model = model;
    this.board = board;
    this.onChange = onChange;
    board.addEventListener("keydown", this.onKey);
  }

  destroy() {
    this.board.removeEventListener("keydown", this.onKey);
  }

  private get view(): UnoView | null {
    return this.model.view;
  }

  private handleKey(e: KeyboardEvent) {
    const view = this.view;
    if (!view || e.ctrlKey || e.altKey || e.metaKey) return;
    if (this.model.picking) return this.handlePicker(e);
    const key = e.key.length === 1 ? e.key.toLowerCase() : e.key;
    let handled = true;
    switch (key) {
      case "ArrowLeft":
        this.moveTo(this.model.cursor - 1);
        break;
      case "ArrowRight":
        this.moveTo(this.model.cursor + 1);
        break;
      case "Home":
        this.moveTo(0);
        break;
      case "End":
        this.moveTo(view.hand.length - 1);
        break;
      case "ArrowDown":
        this.jumpPlayable(1);
        break;
      case "ArrowUp":
        this.jumpPlayable(-1);
        break;
      case "Enter":
      case " ":
        this.playCurrent();
        break;
      case "l":
        this.draw();
        break;
      case "p":
        this.pass();
        break;
      case "v":
        say(`Vrchní karta: ${topText(view)}.`);
        break;
      case "r":
        say(this.statusText());
        break;
      case "t":
        say(orderText(this.view!, this.model.me));
        break;
      case "h":
        say(HELP_TEXT);
        break;
      default:
        handled = false;
    }
    if (handled) e.preventDefault();
  }

  private moveTo(index: number) {
    const view = this.view!;
    if (!view.hand.length) return say("Nemáš žádné karty.");
    if (index < 0 || index >= view.hand.length) {
      // Okraj ruky: zvuk okraje a znovu název karty, na které stojím (jinak by s jedinou kartou
      // šipky nikdy nic nepřečetly).
      play("okraj", { pan: index < 0 ? -0.9 : 0.9 });
      const current = Math.max(0, Math.min(this.model.cursor, view.hand.length - 1));
      this.model.cursor = current;
      return say(cardName(view.hand[current]));
    }
    this.model.cursor = index;
    this.onChange();
    const card = view.hand[index];
    // Jestli jde karta zahrát, řekne zvuk; čtečka jen název karty.
    // Nehratelná karta má běžný zvuk procházení (jako v pexesu), hratelná vlastní.
    play(playable(view, card) ? "uno-hratelna" : "kurzor", { pan: panFor(index, view.hand.length) });
    say(cardName(card));
  }

  private jumpPlayable(dir: 1 | -1) {
    const view = this.view!;
    const n = view.hand.length;
    for (let step = 1; step <= n; step++) {
      const i = (((this.model.cursor + dir * step) % n) + n) % n;
      if (playable(view, view.hand[i])) return this.moveTo(i);
    }
    play("okraj");
    say(view.turn === this.model.me ? "Žádná hratelná karta. Lízni klávesou L." : turnText(view, this.model.me));
  }

  private myTurnOrSay(): boolean {
    const view = this.view!;
    if (view.over) {
      say(resultText(view, this.model.me));
      return false;
    }
    if (view.turn !== this.model.me) {
      play("okraj");
      say(turnText(view, this.model.me));
      return false;
    }
    return true;
  }

  private playCurrent() {
    const view = this.view!;
    if (!this.myTurnOrSay()) return;
    const card = view.hand[this.model.cursor];
    if (!card) return;
    if (!playable(view, card)) {
      play("okraj");
      const why = view.drawnCardId !== null ? "Teď můžeš zahrát jen líznutou kartu, nebo P." : card.kind === "wild4" && view.pendingDraw === 0 ? `Plus čtyři jde zahrát jen když nemáš kartu v barvě ${COLOR_NAME[view.color]}.` : `Vrchní karta: ${topText(view)}.`;
      return say(`${cardName(card)} teď nejde zahrát. ${why}`);
    }
    if (card.color === null) {
      this.model.picking = { cardId: card.id, index: 0 };
      this.onChange();
      return say(`Vyber barvu šipkami nahoru a dolů, Enter potvrdí, Escape zruší. ${COLOR_NAME[COLORS[0]]}.`);
    }
    this.model.sendAction({ type: "play", cardId: card.id });
  }

  private handlePicker(e: KeyboardEvent) {
    const pick = this.model.picking!;
    let handled = true;
    switch (e.key) {
      case "ArrowUp":
      case "ArrowLeft":
      case "ArrowDown":
      case "ArrowRight": {
        const dir = e.key === "ArrowUp" || e.key === "ArrowLeft" ? -1 : 1;
        pick.index = (pick.index + dir + COLORS.length) % COLORS.length;
        const color = COLORS[pick.index];
        play("kurzor");
        say(COLOR_NAME[color]);
        this.onChange();
        break;
      }
      case "Enter":
      case " ":
        this.model.picking = null;
        this.onChange();
        this.model.sendAction({ type: "play", cardId: pick.cardId, color: COLORS[pick.index] });
        break;
      case "Escape":
        this.model.picking = null;
        this.onChange();
        say("Výběr barvy zrušen.");
        break;
      default:
        handled = false;
    }
    if (handled) e.preventDefault();
  }

  private draw() {
    const view = this.view!;
    if (!this.myTurnOrSay()) return;
    if (view.drawnCardId !== null) {
      play("okraj");
      return say("Už máš líznuto. Zahraj líznutou kartu Enterem, nebo P.");
    }
    this.model.sendAction({ type: "draw" });
  }

  private pass() {
    const view = this.view!;
    if (!this.myTurnOrSay()) return;
    if (view.drawnCardId === null) {
      play("okraj");
      return say("P platí jen po líznutí hratelné karty.");
    }
    this.model.sendAction({ type: "pass" });
  }

  rejected() {
    play("okraj");
  }

  statusText(): string {
    const view = this.view;
    if (!view) return "";
    if (view.over) return resultText(view, this.model.me);
    const parts = [`Vrchní karta: ${topText(view)}.`, turnText(view, this.model.me), countsText(view, this.model.me)];
    const hint = hintText(view, this.model.me);
    if (hint) parts.push(hint);
    if (view.players.length > 2) parts.push(view.direction === 1 ? "Směr: po směru." : "Směr: obrácený.");
    return parts.join(" ");
  }

  /** Volá se po aplikaci nového pohledu na model. */
  onEvents(events: GameEvent[]) {
    const view = this.view!;
    const me = this.model.me;
    for (const e of events) {
      switch (e.type) {
        case "started":
          queue(view.turn === me ? "tvuj-tah" : "souper-tah");
          announce(`Hra začala. Máš ${cards(view.hand.length)}. Vrchní karta: ${topText(view)}. ${turnText(view, me)} ${hintText(view, me)} Nápověda klávesou H.`);
          break;
        case "played": {
          const card = e.card as Card;
          queue("uno-zahrani");
          const special = { skip: "uno-stop", reverse: "uno-otocka", draw2: "uno-plus", wild: "uno-zmena", wild4: "uno-plus" }[card.kind as string];
          if (special) queue(special);
          const colorPart = card.color ? "" : `, barva ${COLOR_NAME[e.color as Color]}`;
          if (e.by === me) announce(`Zahráno: ${cardName(card)}${colorPart}.`);
          else announce(`${playerName(view, e.by as string, me)}: ${cardName(card)}${colorPart}. Zbývá ${cards(e.cardsLeft as number)}.`);
          break;
        }
        case "drew":
          queue("uno-lizani");
          if (e.by !== me) announce(`${playerName(view, e.by as string, me)}: líže ${cards(e.count as number)}${e.penalty ? " za trest" : ""}.`);
          break;
        case "drewCards": {
          const list = (e.cards as Card[]).map(cardName).join(", ");
          announce(list ? `Nové karty: ${list}.` : "Balíček je prázdný.");
          break;
        }
        case "canPlayDrawn": {
          const i = view.hand.findIndex((c) => c.id === (e.card as Card).id);
          if (i >= 0) this.model.cursor = i;
          this.onChange();
          announce("Líznutou kartu můžeš zahrát Enterem, nebo ji nech klávesou P.");
          break;
        }
        case "skipped":
          announce(e.player === me ? "Stojíš." : `${playerName(view, e.player as string, me)} stojí.`);
          break;
        case "reversed":
          announce("Směr hry se otočil.");
          break;
        case "passed":
          if (e.by !== me) announce(`${playerName(view, e.by as string, me)}: kartu si nechává.`);
          break;
        case "reshuffled":
          announce("Balíček se zamíchal.");
          break;
        case "uno":
          queue("uno-volani");
          announce(e.player === me ? "UNO! Máš poslední kartu." : `${playerName(view, e.player as string, me)}: UNO, poslední karta!`);
          break;
        case "turn":
          queue(e.player === me ? "tvuj-tah" : "souper-tah");
          if (e.player === me) announce(`Jsi na tahu. Vrchní karta: ${topText(view)}. ${hintText(view, me)}`);
          else announce(turnText(view, me));
          break;
        case "gameOver": {
          const won = (e.winners as string[]).includes(me);
          queue(won ? "vyhra" : "prohra");
          announce(resultText(view, me));
          break;
        }
      }
    }
  }
}
